import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { AnimatePresence } from "framer-motion";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { AccountPage } from "./pages/AccountPage";
import { RouteTransition } from "./components/RouteTransition";
import { AppLayout } from "./components/app/AppLayout";
import { StaffLayout } from "./components/staff/StaffLayout";
import { SiteLayout } from "./components/SiteLayout";
import { AccessGuardProvider } from "./context/AccessGuardContext";
import { CartProvider } from "./context/CartContext";
import { CustomerProvider } from "./context/CustomerContext";
import { MenuProvider } from "./context/MenuContext";
import { StaffOrdersProvider } from "./context/StaffOrdersContext";

const AboutPage = lazy(async () => import("./pages/AboutPage").then((module) => ({ default: module.AboutPage })));
const CateringPage = lazy(async () =>
  import("./pages/CateringPage").then((module) => ({ default: module.CateringPage }))
);
const DeliveryPage = lazy(async () =>
  import("./pages/DeliveryPage").then((module) => ({ default: module.DeliveryPage }))
);
const HomePage = lazy(async () => import("./pages/HomePage").then((module) => ({ default: module.HomePage })));
const LegalPage = lazy(async () => import("./pages/LegalPage").then((module) => ({ default: module.LegalPage })));
const MenuPage = lazy(async () => import("./pages/MenuPage").then((module) => ({ default: module.MenuPage })));
const NotFoundPage = lazy(async () =>
  import("./pages/NotFoundPage").then((module) => ({ default: module.NotFoundPage }))
);
const OrderPage = lazy(async () => import("./pages/OrderPage").then((module) => ({ default: module.OrderPage })));
const OwnerMenuPage = lazy(async () =>
  import("./pages/owner/OwnerMenuPage").then((module) => ({ default: module.OwnerMenuPage }))
);
const AppCartPage = lazy(async () =>
  import("./pages/app/AppCartPage").then((module) => ({ default: module.AppCartPage }))
);
const AppHomePage = lazy(async () =>
  import("./pages/app/AppHomePage").then((module) => ({ default: module.AppHomePage }))
);
const AppMenuPage = lazy(async () =>
  import("./pages/app/AppMenuPage").then((module) => ({ default: module.AppMenuPage }))
);
const AppOrdersPage = lazy(async () =>
  import("./pages/app/AppOrdersPage").then((module) => ({ default: module.AppOrdersPage }))
);
const AppProfilePage = lazy(async () =>
  import("./pages/app/AppProfilePage").then((module) => ({ default: module.AppProfilePage }))
);
const StaffHomePage = lazy(async () =>
  import("./pages/staff/StaffHomePage").then((module) => ({ default: module.StaffHomePage }))
);
const StaffKitchenPage = lazy(async () =>
  import("./pages/staff/StaffKitchenPage").then((module) => ({ default: module.StaffKitchenPage }))
);
const StaffOrderDetailPage = lazy(async () =>
  import("./pages/staff/StaffOrderDetailPage").then((module) => ({ default: module.StaffOrderDetailPage }))
);
const StaffOrdersPage = lazy(async () =>
  import("./pages/staff/StaffOrdersPage").then((module) => ({ default: module.StaffOrdersPage }))
);
const StaffProfilePage = lazy(async () =>
  import("./pages/staff/StaffProfilePage").then((module) => ({ default: module.StaffProfilePage }))
);

function RouteShellFallback() {
  return (
    <div className="min-h-[100dvh] px-4 py-6">
      <div className="mx-auto max-w-[1400px]">
        <div className="space-y-4">
          <div className="h-4 w-28 animate-pulse rounded-full bg-stone-900/8" />
          <div className="h-16 max-w-xl animate-pulse rounded-[2rem] bg-stone-900/8" />
          <div className="grid gap-3">
            <div className="h-44 animate-pulse rounded-[2rem] bg-white/70" />
            <div className="h-44 animate-pulse rounded-[2rem] bg-white/70" />
          </div>
        </div>
      </div>
    </div>
  );
}

function resolveNativeAppRoute(url: string) {
  try {
    const parsed = new URL(url);

    if (parsed.protocol === "http:" || parsed.protocol === "https:") {
      if (
        parsed.pathname.startsWith("/app/") ||
        parsed.pathname.startsWith("/staff/") ||
        parsed.pathname === "/order" ||
        parsed.pathname === "/account"
      ) {
        return `${parsed.pathname}${parsed.search}${parsed.hash}`;
      }

      return null;
    }

    if (!parsed.host) {
      return null;
    }

    return `/${parsed.host}${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

function NativeAppUrlHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const navigateToAppUrl = (url: string) => {
      const target = resolveNativeAppRoute(url);

      if (target) {
        void Browser.close().catch(() => undefined);
        navigate(target, { replace: true });
      }
    };

    void CapacitorApp.getLaunchUrl().then((result) => {
      if (result?.url) {
        navigateToAppUrl(result.url);
      }
    });

    const handle = CapacitorApp.addListener("appUrlOpen", (event) => {
      navigateToAppUrl(event.url);
    });

    return () => {
      void handle.then((listener) => listener.remove());
    };
  }, [navigate]);

  return null;
}

function AnimatedRoutes() {
  const location = useLocation();
  const isNativeApp = Capacitor.isNativePlatform();
  const nativeShellMode = import.meta.env.VITE_NATIVE_SHELL_MODE === "staff" ? "staff" : "customer";
  const nativeHomePath = nativeShellMode === "staff" ? "/staff/dispatch" : "/app/home";
  const routes = (
    <Suspense fallback={<RouteShellFallback />}>
      <Routes location={location} key={location.pathname}>
        <Route path="/app" element={<AppLayout />}>
          <Route index element={<Navigate to="/app/home" replace />} />
          <Route path="home" element={<AppHomePage />} />
          <Route path="menu" element={<AppMenuPage />} />
          <Route path="cart" element={<AppCartPage />} />
          <Route path="orders" element={<AppOrdersPage />} />
          <Route path="profile" element={<AppProfilePage />} />
        </Route>
        <Route
          path="/staff"
          element={
            <StaffOrdersProvider>
              <StaffLayout />
            </StaffOrdersProvider>
          }
        >
          <Route index element={<Navigate to="/staff/dispatch" replace />} />
          <Route path="dispatch" element={<StaffHomePage />} />
          <Route path="orders" element={<StaffOrdersPage />} />
          <Route path="orders/:orderId" element={<StaffOrderDetailPage />} />
          <Route path="kitchen" element={<StaffKitchenPage />} />
          <Route path="profile" element={<StaffProfilePage />} />
        </Route>
        <Route element={<SiteLayout />}>
          <Route path="/" element={isNativeApp ? <Navigate to={nativeHomePath} replace /> : <HomePage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/menu" element={<MenuPage />} />
          <Route path="/catering" element={<CateringPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/delivery" element={<DeliveryPage />} />
          <Route path="/order" element={<OrderPage />} />
          <Route path="/owner/menu" element={<OwnerMenuPage />} />
          <Route path="/privacy" element={<LegalPage />} />
          <Route path="/offer" element={<LegalPage />} />
          <Route path="/terms" element={<LegalPage />} />
          <Route
            path="*"
            element={
              <RouteTransition>
                <NotFoundPage />
              </RouteTransition>
            }
          />
        </Route>
      </Routes>
    </Suspense>
  );

  if (isNativeApp) {
    return routes;
  }

  return (
    <AnimatePresence mode="wait">
      {routes}
    </AnimatePresence>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <NativeAppUrlHandler />
      <AccessGuardProvider>
        <CustomerProvider>
          <MenuProvider>
            <CartProvider>
              <AnimatedRoutes />
            </CartProvider>
          </MenuProvider>
        </CustomerProvider>
      </AccessGuardProvider>
    </BrowserRouter>
  );
}
